// Pure helpers for App.jsx's periodic incremental conversation-list poll —
// extracted so the merge/sort logic (no React harness available) can be
// unit-tested with node --test.

import { DEFAULT_INTENT_PRIORITY_TIER, conversationMatchesToggles } from '@cxhub/shared'

// priorities: the Queue's priority levels as GET /api/intent-priorities
// returns them — { tiers: { <intent>: 0-3 }, defaultTier } — the SAME table
// (intent_priorities, migration 0093) the server's own ORDER BY reads
// (repositories/conversations.js's intentPrioritySql()), so this re-sort,
// which runs on every 10s poll tick over the WHOLE visible list, orders the
// way a fresh full load would. No level is coded here; until the map has
// loaded every intent reads the default level, so the list stays in plain
// oldest-first order rather than a guessed one. intentOverride wins over the
// classifier's latestIntent, same precedence as the SQL's
// COALESCE(c.intent_override, c.latest_intent).
//
// The row's own server-computed level wins when present (c.priorityTier,
// 2026-10-29): it is the intent level, lowered by a quick-reply rule when
// the customer answered our message within the rule's window
// (docs/decisions/2026-10-29-quick-reply-priority-rule.md). The incremental
// poll returns fresh rows from the same query, so a merged row carries its
// recomputed level too. The intent map is the fallback for a row without one.
export function priorityTier(c, priorities = null) {
  if (Number.isInteger(c.priorityTier)) return c.priorityTier
  const intent = c.intentOverride ?? c.latestIntent
  const tier = priorities?.tiers?.[intent]
  return Number.isInteger(tier) ? tier : (priorities?.defaultTier ?? DEFAULT_INTENT_PRIORITY_TIER)
}

// Mirrors the SQL's "COALESCE(earliest_unanswered_inbound_at, last_message_at)
// ASC/DESC NULLS LAST" for whichever direction is requested (repositories/
// conversations.js's listConversations()) — the open Queue's basis is the
// earliest still-unanswered inbound message, not just the most recent
// message in either direction (a buyer messaging twice in a row with no
// reply between must sort/display by the FIRST of those two). closedOnly
// mirrors the server's own gate: Closed stays on plain lastMessageAt
// regardless of direction, since awaitingSince is already computed as
// exactly lastMessageAt for closed conversations server-side (see
// mapConversation's own comment) — kept as an explicit param here too rather
// than relying on that always holding. A missing value always sorts to the
// END regardless of direction (ascending: treated as +Infinity; descending:
// treated as -Infinity, so a real timestamp always outranks it).
function compareByTime(a, b, direction, closedOnly) {
  const basis = (c) => (closedOnly ? c.lastMessageAt : (c.awaitingSince ?? c.lastMessageAt))
  const missingSortValue = direction === 'desc' ? -Infinity : Infinity
  const aVal = basis(a), bVal = basis(b)
  const aTime = aVal ? new Date(aVal).getTime() : missingSortValue
  const bTime = bVal ? new Date(bVal).getTime() : missingSortValue
  return direction === 'desc' ? bTime - aTime : aTime - bTime
}

// sortBy mirrors the server's own listConversations() branch: 'oldest'
// (default) keeps the priority-level tiering (see priorityTier()),
// oldest-to-newest within each tier; 'newest' ignores tiering entirely —
// strictly newest-at-top for every intent. closedOnly mirrors the server's
// own closedOnly gate: when the current status filter is Closed-only,
// tiering is skipped even on the 'oldest' branch, since prioritizing open
// work is meaningless once everything in view is already closed.
// Escalated first (2026-10-07): outside Closed, every escalated contact sorts
// above every other under both sort orders, priority-0 intents included —
// the same CASE the server's ORDER BY leads with.
export function conversationComparator(a, b, sortBy = 'oldest', closedOnly = false, priorities = null) {
  if (!closedOnly) {
    const escalatedDiff = (a.status === 'escalated' ? 0 : 1) - (b.status === 'escalated' ? 0 : 1)
    if (escalatedDiff !== 0) return escalatedDiff
  }
  if (sortBy === 'newest') return compareByTime(a, b, 'desc', closedOnly)
  if (!closedOnly) {
    const tierDiff = priorityTier(a, priorities) - priorityTier(b, priorities)
    if (tierDiff !== 0) return tierDiff
  }
  return compareByTime(a, b, 'asc', closedOnly)
}

// Merges freshly-polled `changed` rows into the already-loaded `prev` array
// IN PLACE (never a full replace) — an id already present is replaced with
// its fresh copy (covers status/intent/last_message_at changes on an
// already-loaded conversation), an id not yet present is appended (covers a
// brand-new conversation) — then the WHOLE array is re-sorted with the same
// comparator the server's own ORDER BY produces (for the CSR's currently
// selected sortBy), so already-loaded "Load More" pages stay intact and
// ordering matches what a fresh full load would give. A no-op (returns
// `prev` as-is) when nothing changed, so an empty poll response never even
// triggers a re-render.
//
// allowedStatuses (optional): when provided, any row (existing OR freshly
// merged) whose status is no longer in this list is dropped from the
// result entirely — the CSR's own poll (App.jsx's tick()) intentionally
// queries WITHOUT a status filter so it can see a conversation transition
// OUT of the current view (e.g. another agent closing it), not just changes
// within it; this is the other half of that fix, since merging that fresh
// row in without also removing it would otherwise just update its status
// in place and leave it visible. Omit to keep every row regardless of
// status, same behavior as before this param existed.
//
// toggles (optional, 2026-10-07): the Queue's 1A / AM / Support selection —
// a merged row the toggle rule (conversationMatchesToggles(), the server's
// rule too) no longer includes is dropped, so a live update can't add a row
// to the wrong view.
// viewer (2026-10-14): who is looking, for the task contacts' toggle rule
// (queueToggles.js's toggleViewer()).
export function mergeAndSort(prev, changed, sortBy = 'oldest', closedOnly = false, allowedStatuses = null, priorities = null, toggles = null, viewer = null) {
  if (!changed?.length) return prev
  const byId = new Map(prev.map((c) => [c.id, c]))
  for (const c of changed) byId.set(c.id, c)
  let merged = [...byId.values()]
  if (allowedStatuses?.length) merged = merged.filter((c) => allowedStatuses.includes(c.status))
  if (toggles) merged = merged.filter((c) => conversationMatchesToggles(c, toggles, viewer))
  return merged.sort((a, b) => conversationComparator(a, b, sortBy, closedOnly, priorities))
}

// Whether a new priorities map from the session tick should reload the Queue
// list in full (2026-10-29). Each Queue row carries its own server-computed
// level (priorityTier), so a changed intent level or quick-reply rule only
// shows on loaded rows after a fresh load. The tick's `version` moves when
// either changes. The first map to arrive (previous null) never reloads, and
// neither does a map from a server without a version.
export function shouldReloadQueueForPriorities(previous, next) {
  if (!previous || !next) return false
  if (next.version == null) return false
  return previous.version !== next.version
}
